import { can, logError } from '@/composables';
import { showConfirm, showToast } from '@/composables/swal';
import {
  ActivityItem,
  ApiResponse,
  ApiResponseV1,
  ApiResponse2,
  Company,
  DashboardRequest,
  DashboardResult,
  DashboardTab,
  Factory,
  LockerRoom,
  MenuItem,
  MerchantUsers,
  NavigationMenuItem,
  Order,
  OrderAdminUser,
  OrderDetail,
  OrderActivityLogDetail,
  OrderActivityLogListItem,
  Result,
  Role,
  User,
} from '@/models';
import { AxiosResponse } from 'axios';
import { createPinia, defineStore } from 'pinia';
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate';
import { createApp, reactive, ref } from 'vue';
import { handleResponseException, http } from '../interceptors/axiosRequestInterceptor';
import {
  toDetail as mapOrderActivityLogDetail,
  toListItem as mapOrderActivityLogListItem,
  type BackendOrderActivityLog,
} from '@/utils/orderActivityLogMapper';
import { buildGetCompaniesQueryString } from '@/utils/getCompaniesQuery';
import { CSManager } from '@/models/CSManager';
import { salesRepresentative } from '@/models/salesRepresentative';
import { Shop } from '@/models/Shop';
const app = createApp({});
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
type Urgency = 'vip' | 'urgent' | 'extra_attention';

export const useMainStore = defineStore('main', {
  state: () => ({
    navigation: ref<NavigationMenuItem[]>([]),
    user: ref<User | undefined>(undefined),
    loading: ref<boolean>(false),
    loadingNavigation: ref<boolean>(false),
    globalLoader: ref<boolean>(false),
    routeInfo: ref<NavigationMenuItem | null>(null),
    permissions: ref<Array<string> | undefined>([]),
    mobileDevice: ref<boolean>(false),
    sidebarOpen: ref<boolean>(true),
    role: ref<Role | undefined>(undefined),
    activity_item: ref<ActivityItem>(null),
    dashboardData: reactive<DashboardResult>({
      order_items_by_status_group: [],
      items: {
        current_page: 1,
        data: [],
        first_page_url: '',
        from: 0,
        last_page: 0,
        last_page_url: '',
        links: [],
        next_page_url: '',
        path: '',
        per_page: '',
        prev_page_url: '',
        to: 0,
        total: 0,
      },
      tab_count: {
        pending_merchant_approval_items_count: 0,
        pending_for_factory_assignment_count: 0,
        urgent_action_items_count: 0,
        customer_await_items_count: 0,
        quote_requested_items_count: 0,
        pending_items_count: 0,
        completed_items_count: 0,
        cancelled_items_count: 0,
        shipped_items_count: 0,
        reorders_count: 0,
        unresolved_comments_count: 0,
        all_items_count: 0,
        awaiting_customer_feedback_count: 0
      },
    }),
    dashboardTabs: ref<DashboardTab[]>([]),
    dashboardRequest: reactive<DashboardRequest>({
      items_for: 'all_items',
      sort_by: 'order_no',
      sort_order: 'desc',
      page: 1,
      search: '',
      per_page: 10,
    }),
    permissionLoaded: ref<boolean>(false),
    factories: ref<Factory[]>([]),
    orderAdmins: ref<OrderAdminUser[]>([]),
    csManagers: ref<CSManager[]>([]),
    salesRepresentatives: ref<User[]>([]),
    lockerRoom: ref<LockerRoom[] | undefined>([]),
    merchantUsers: ref<MerchantUsers[]>([]),
    shops: ref<Shop[]>([]),
    unassignedOrders: ref<Order[]>([]),
    companies: ref<Company[]>([]),
    customers: ref<[]>([]),
    dashboardQuickFilters: ref<Record<
      string,
      string | Array<string | number> | Record<'to' | 'from', string>
    > | null>(JSON.parse(localStorage.getItem('dashboardQuickFilters') || 'null')),
  }),
  actions: {
    async getNavigation() {
      this.loadingNavigation = true;
      try {
        const response: AxiosResponse<ApiResponseV1<MenuItem[]>> = await http.get('navigation');
        const navigationMenu: NavigationMenuItem[] = [];
        response.data.result.filter(item => item.name !== "Dashboard").forEach((data: MenuItem) => {
          let currentMenu: NavigationMenuItem = {
            name: '',
            to: '',
            icon: '',
            icon_id: data.icon_id || '',
            children: [],
          };
          if (data.children_menu.length === 0) {
            currentMenu.name = data.name;
            currentMenu.to = data.url_path;
            currentMenu.icon_id = data.icon_id || '';
          } else {
            currentMenu.name = data.name;
            currentMenu.to = data.url_path;
            currentMenu.icon_id = data.icon_id || '';
            currentMenu.children = data.children_menu.map((item) => ({
              name: item.name,
              to: item.url_path,
              icon_id: item.icon_id || '',
            }));
          }
          navigationMenu.push(currentMenu);
        });

        if(can('default-products')){
          const defaultProducts = {
            name: 'Default Products',
            to: null,
            icon: 'box-open',
            icon_id: "Shirt",
            children: [
              {
                name: 'Default Categories',
                to: '/default-categories',
                icon_id: null,
              },

              {
                name: 'Assign Products',
                to:'/default-products',
                icon_id: null,
              },
            ],
          }
          // find index of "Products"
          const productsIndex = navigationMenu.findIndex(
            item => item.name === 'Products'
          )

          // insert right after "Products"
          if (productsIndex !== -1) {
            navigationMenu.splice(productsIndex + 1, 0, defaultProducts)
          } else {
            // fallback: push at end if Products not found
            navigationMenu.push(defaultProducts)
          }
        }

        if(can('resellers-management')){
          const resellersManagement = {
            name: 'Resellers Management',
            to: null,
            icon: 'box-open',
            icon_id: "Shirt",
            children: [
              {
                name: 'Resellers',
                to: '/resellers-management',
                icon_id: null,
              },


            ],
          }
          // find index of "Acl Management"
          const aclManagementIndex = navigationMenu.findIndex(
            item => item.name === 'Acl Management'
          )

          // insert right after "Acl Management"
          if (aclManagementIndex !== -1) {
            navigationMenu.splice(aclManagementIndex + 1, 0, resellersManagement)
          } else {
            // fallback: push at end if Acl Management not found
            navigationMenu.push(resellersManagement)
          }
        }

        if (can('access-old-dashboard')) {  // replace with your actual condition
          const dashboardMenu: NavigationMenuItem = {
            name: 'Dashboard (legacy)',
            to: '/dashboard',
            icon: '',
            icon_id: null,
            children: [],
          };

          // Push at the beginning or a specific position
          navigationMenu.unshift(dashboardMenu); // adds at the top
        }

        this.navigation = navigationMenu;
      } catch (err: any) {
        handleResponseException(err);
      } finally {
        this.loadingNavigation = false;
      }
    },
    async getCurrentUser() {
      this.loading = true;
      try {
        const userResponse: AxiosResponse<ApiResponse2<Result<User>>> = await http.get('authuser');
        if (userResponse.status === 200) {
          this.user = userResponse.data.result.user;
          this.role = this.user?.roles[0];
        }
      } catch (err: any) {
        handleResponseException(err);
      }
      this.loading = false;
    },
    async getPermissions() {
      this.permissionLoaded = false;
      try {
        const permissions_response: AxiosResponse<ApiResponse2<Result<Array<string>>>> =
          await http.get(`permissions`);
        if (permissions_response) {
          const response_data = permissions_response.data;
          this.permissions = response_data.result.permissions;
          this.permissionLoaded = true;
        }
      } catch (err: any) {
        handleResponseException(err);
      }
    },
    async getDashboardData(
      data: DashboardRequest,
      payload: Record<string, string | Array<string | number> | Record<any, any>> | null = null
    ) {
      this.dashboardRequest = data;
      this.loading = true;
      try {
        const queryParams = new URLSearchParams(data as any).toString();

        const requestBody = payload ? { quick_filters: payload } : {};
        const response: AxiosResponse<ApiResponse2<DashboardResult>> = await http.post(
          `dashboard?${queryParams}`,
          requestBody
        );
        if (response) {
          const response_data = response.data;
          this.dashboardData = response_data.result;
          this.dashboardTabs = Object.entries(this.dashboardData.tab_count).map(([key, count]) => {
            const cleanedKey = key.replace(/_(items?|count)/gi, '');
            return {
              display_name: cleanedKey
                .replace(/_/g, ' ')
                .replace(/\b\w/g, (char) => char.toUpperCase())
                .trim(),
              count: count ?? 0,
              key,
              cleaned_key: cleanedKey,
              apiKey: key.replace('_count', ''),
            };
          });
        }
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
      this.loading = false;
    },
    async getOrderDetail(order_id: number, order_item_id?: number) {
      try {
        const response: AxiosResponse<ApiResponse2<OrderDetail>> = await http.get(
          `customer-orders/${order_id}${order_item_id ? `?order_item_id=${order_item_id}` : ''}`
        );
        if (response) {
          return response.data.result;
        }
      } catch (err: any) {
        handleResponseException(err);
      }
      return null;
    },

    /**
     * GET /order-items/{order_item_id}/order-activity-logs
     * Backend may wrap the list as `result.order_activity_logs` (Laravel resource style)
     * or return `result` as a plain array. Handle both.
     */
    async getOrderActivityLogs(order_item_id: number): Promise<OrderActivityLogListItem[]> {
      try {
        const res = await http.get<
          ApiResponse2<
            | BackendOrderActivityLog[]
            | { order_activity_logs: BackendOrderActivityLog[] }
          >
        >(`order-items/${order_item_id}/order-activity-logs`);
        const result = res.data?.result;
        const rows: BackendOrderActivityLog[] = Array.isArray(result)
          ? result
          : Array.isArray((result as any)?.order_activity_logs)
            ? (result as { order_activity_logs: BackendOrderActivityLog[] }).order_activity_logs
            : [];
        if (!rows.length) {
          return [];
        }
        return rows.map(mapOrderActivityLogListItem);
      } catch (err) {
        logError('getOrderActivityLogs failed', err);
        throw err;
      }
    },

    /**
     * GET /order-activity-logs/{activity_log_id}
     * Backend wraps the row as `result.order_activity_log`.
     */
    async getOrderActivityLogDetail(
      order_item_id: number,
      activity_log_id: string | number,
    ): Promise<OrderActivityLogDetail | null> {
      try {
        const res = await http.get<
          ApiResponse2<{ order_activity_log: BackendOrderActivityLog } | BackendOrderActivityLog>
        >(`order-activity-logs/${activity_log_id}`);
        const result = res.data?.result;
        const row =
          result && typeof result === 'object' && 'order_activity_log' in result
            ? (result as { order_activity_log: BackendOrderActivityLog }).order_activity_log
            : (result as BackendOrderActivityLog | undefined);
        if (!row) {
          return null;
        }
        return mapOrderActivityLogDetail(row);
      } catch (err) {
        logError('getOrderActivityLogDetail failed', err);
        throw err;
      }
    },

    async getFactories() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse<Factory[]>>('dashboard-factories');
        if (res.status === 200) {
          this.factories = res.data.data;
          this.loading = false;
        }
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },
    async getOrderAdmins(role: string | string[] = 'orderAdministrator') {
      this.loading = true;
      try {
        const roles = Array.isArray(role) ? role : [role];
        const roleParams = roles.map(r => `role[]=${encodeURIComponent(r)}`).join('&');

        const res = await http.get<ApiResponse2<Result<OrderAdminUser[]>>>(
          `role/users?${roleParams}`
        );
        if (res.status === 200 && res.data.result.users) {
          this.orderAdmins = res.data.result.users;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },
    async getCSManagers() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse2<Result<CSManager[]>>>(
          'role/users?role=csManager'
        );
        if (res.status === 200 && res.data.result.users) {
          this.csManagers = res.data.result.users;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },
    async getSalesRepresentatives() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse2<Result<salesRepresentative[]>>>(
          'role/users?role=salesRepresentative'
        );
        if (res.status === 200 && res.data.result.users) {
          this.salesRepresentatives = res.data.result.users;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },
    async getAllMerchants() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse2<MerchantUsers[]>>(
          `get-companies${buildGetCompaniesQueryString(this.role)}`
        );
        if (res.status === 200 && res.data.result) {
          this.merchantUsers = res.data.result;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },

    async getAllShops() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse2<Shop[]>>('get-shops');
        if (res.status === 200 && res.data.result) {
          this.shops = res.data.result;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },

    async getUnassignedOrders() {
      this.loading = true;
      try {
        const res = await http.get<ApiResponse2<Result<Order[]>>>(
          'get_order_administrator_unassinged_orders'
        );
        if (res.status === 200 && res.data.result.orders) {
          this.unassignedOrders = res.data.result.orders;
        }
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        handleResponseException(err);
      }
    },

    async fetchCustomTechpackFiles(orderId) {
      return await http.get(`orders/${orderId}/custom-techpack-files`);
    },

    async uploadMultipleCustomTechpackFiles(formData, orderId) {
      return await http.post(`orders/${orderId}/custom-techpack-files`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },

    async deleteSingleCustomTechpackFile(orderId, fileName) {
      return await http.delete(`orders/${orderId}/custom-techpack-files/${fileName}`);
    },


    async notifyCustomerForQuote(order_id: number) {
      try {
        this.loading = true;
        const response = await http.get<ApiResponse2<any>>(`notify-customer-quote/${order_id}`);
        this.loading = false;
        if (response.data.message) {
          showToast('Email has been sent to customer.', 'success');
        }
      } catch (e: any) {
        this.loading = false;
        handleResponseException(e);
      }
    },
    async updateOrderFactory(payload: any) {
      this.loading = true;
      try {
        const { order_id } = payload;
        const res = await http.put(`order/${order_id}/update-factory`, payload);
        showToast(res.data.message, 'success');
        return res;
      } catch (error) {
        showToast(error.response.data.message, 'error');
        this.loading = true;
        throw error;
      }
    },
    async updateOrderUrgency(urgency: Urgency, orderId: number | string) {
      this.loading = true;
      try {
        const payload = {
          order_id: orderId,
          urgency: urgency,
        };
        const res = await http.post(`update/order/urgency`, payload);
        showToast(res.data.message, 'success');
        return res;
      } catch (error) {
        this.loading = false;
        logError('Error downloading the file:', error);
      }
    },
    async exportOrderPrices(params: DashboardRequest, quickFilters: Record<string, string | Array<string | number> | Record<'to' | 'from', string>> | null, payload: { fromDate: string | null; endDate: string | null; exportType?: string })
    {

      this.dashboardRequest = params;
      this.loading = true;
      const requestBody = quickFilters ? { quick_filters: quickFilters } : {};
      try {
        const queryParams = new URLSearchParams(params as any).toString();

        const response = await http.post(
          `/export-order-prices?${queryParams}`,
          {
            ...requestBody,
            from_date: payload.fromDate,
            end_date: payload.endDate,
            export_type: payload.exportType || 'order_item',
          },
          {
            responseType: 'blob',
          }
        );
        const exportType = payload.exportType || 'order_item';
        const fileName = `${exportType}_prices.xlsx`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        this.loading = false;
      } catch (error) {
        this.loading = false;
        logError('Error downloading the file:', error);
      }
    },
    async assignFactory(payload: any) {
      this.loading = true;
      try {
        const response = await http.post<ApiResponse2<any>>(`/assign_factory`, payload);
        if (response.status === 200) {
          this.loading = false;
          if (response.data.message) {
            showToast(response.data.message, 'success');
          }
        }
      } catch (err: any) {
        handleResponseException(err);
        showToast(err.response.data.message, 'error');
        this.loading = false;
      }
    },
    async updateOrderAdmin(payload: { user_id: string | number | null; order_ids: number | any }) {
      this.loading = true;
      try {
        const response = await http.post<ApiResponse2<any>>('assign_order_to', payload);
        if (response.status === 200) {
          showToast('Order admin assigned', 'success');
          this.loading = false;
        } else {
          showToast(response.data.message, 'error');
        }
      } catch (err: any) {
        this.loading = false;
        showToast(err.response.data.message, 'error');
        handleResponseException(err);
      }
      this.loading = false;
    },
    async deleteOrder(orderID: number | string) {
      let confirmDelete = await showConfirm('', 'Are you sure that you want to delete this order?');
      if (confirmDelete) {
        try {
          this.loading = true;

          await http.delete(`order/delete/${orderID}`).then(async (response) => {
            if (response.data.success && response.data.status_code === 200) {
              showToast(response.data.message, 'success');
              this.loading = false;
            } else {
              this.loading = false;
            }
          });
        } catch (e) {
          logError(e);
          this.loading = false;
        }
      }
    },
     async updateOrderDQCStatus(orderID: number | string, statusPayload: { dqc_approved: boolean }) {
      await http.put(`order/${orderID}/dqc-status`, statusPayload).then(async (response) => {
        if (response.data.success && response.data.status_code === 200) {
          showToast(response.data.message, 'success');
          this.loading = false;
        } else {
          this.loading = false;
        }
      });
    },

    async getCustomerLockerRoom(companyId: number, customerId: number) {
      try {
        const response = await http.get<ApiResponse2<Result<LockerRoom[]>>>(
          `customer_lockers_rosters?company_id=${companyId}&customer_id=${customerId}`
        );
        if (response.status === 200) {
          this.lockerRoom = response.data.result.customer_locker_rooms;
        }
        this.loading = false;
      } catch (err: any) {
        handleResponseException(err);
      }
    },
    async logout() {
      try {
        const response = await http.get('logout');
        if (response.status === 200) {
          localStorage.clear();
          window.location.href = import.meta.env.VITE_VUE2_APP_URL + '/login';
        }
      } catch (err: any) {
        handleResponseException(err);
      }
    },
  },
  getters: {},
  persist: {
    key: 'dashboardRequest',
    pick: ['dashboardRequest'],
  },
});
